import { useEffect, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { hasAtLeast } from 'remeda'
import { Toaster } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import { GoogleOneTapLoginHelper } from '@helpers'
import { allProductsCategoryTranslations } from '@velasca/constants'
import { useCategoryStore } from '@velasca/hooks/use-category-store'
import type { Navbar } from '@velasca/types'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import DesktopNavigation from '../DesktopNavigation'
import { MobileNavigation } from '../MobileNavigation/MobileNavigation'
import buildMenuColumns from './utils/buildMenuColumns'
import clearMobileMenuEntries from './utils/clearMobileMenuEntries'
import { getChildrenByUrl } from './utils/getChildrenByUrl'
import { getMacroCategoriesChildren } from './utils/getMacroCategoriesChildren'

const Header = ({
  menu,
  lowerMenu,
  promotionalBanner,
  miscHeaderData,
  minisearch,
  sticky,
  reduced,
}: Navbar) => {
  const isMinWidth1600px = useMediaQuery('(min-width: 1600px)')

  useEffect(() => {
    const promotionalBannerSkeletonEl = document.querySelector(
      '#promotional-banner-skeleton',
    )

    if (promotionalBannerSkeletonEl) {
      promotionalBannerSkeletonEl.classList.add('react-loaded')
    }
    const headerSkeletonEl = document.querySelector('#header-skeleton')

    if (headerSkeletonEl) {
      headerSkeletonEl.classList.add('react-loaded')
    }
  }, [])

  const desktopMenu = useMemo(
    () =>
      clearMobileMenuEntries(menu)?.map((entry) => {
        if (entry.children) {
          let columns = 4

          if (!isMinWidth1600px && entry.children.length <= 3) {
            columns = 2
          } else if (entry.children.length <= 7) {
            columns = 3
          }

          return {
            ...entry,
            cols: buildMenuColumns(entry.children, columns),
          }
        }

        return entry
      }),
    [isMinWidth1600px, menu],
  )

  const setCurrentCategoryChildren = useCategoryStore(
    (state) => state.setCurrentCategoryChildren,
  )

  const setCurrentCategoryIsContainerCategory = useCategoryStore(
    (state) => state.setCurrentCategoryIsContainerCategory,
  )

  const currentCategory = useCategoryStore((state) => state.currentCategory)

  useEffect(() => {
    if (desktopMenu) {
      const isAllProductsCategory = allProductsCategoryTranslations.includes(
        currentCategory.name,
      )

      setCurrentCategoryChildren(
        (isAllProductsCategory
          ? getMacroCategoriesChildren(desktopMenu)
          : getChildrenByUrl(desktopMenu, window.location.pathname)) || [],
      )
    }
  }, [setCurrentCategoryChildren, desktopMenu, currentCategory.name])

  useEffect(() => {
    if (window.location.pathname.includes('collections')) {
      setCurrentCategoryIsContainerCategory(
        hasAtLeast(currentCategory.children || [], 1),
      )
    }
  }, [currentCategory.children, setCurrentCategoryIsContainerCategory])

  const isDesktop = useMediaQuery('(min-width: 1200px)')

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <GoogleOneTapLoginHelper />
      </ErrorBoundary>
      <ErrorBoundary fallback={null}>
        <Toaster position="top-center" />
      </ErrorBoundary>
      {isDesktop ? (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <DesktopNavigation
            menu={desktopMenu}
            miscHeaderData={miscHeaderData}
            minisearch={minisearch}
            promotionalBanner={promotionalBanner}
            sticky={sticky}
            reduced={reduced}
          />
        </ErrorBoundary>
      ) : (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <MobileNavigation
            menu={menu}
            lowerMenu={lowerMenu}
            miscHeaderData={miscHeaderData}
            minisearch={minisearch}
            promotionalBanner={promotionalBanner}
            sticky={sticky}
          />
        </ErrorBoundary>
      )}
    </>
  )
}

export default Header
